import React, { useState, useEffect, Fragment, useMemo, forwardRef, useImperativeHandle } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { InboxIcon, XMarkIcon, UserCircleIcon, SparklesIcon, NewspaperIcon, ClipboardDocumentListIcon, ClockIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { Button, Alert, Spinner } from './ui';
import TaskForm from './forms/TaskForm';
import ExpectationForm from './forms/ExpectationForm';
import ComposeEmail from './ComposeEmail';
import EmailDetailModal from './EmailDetailModal';
import { EmailAccount } from '../types/email';

// Interface for EmailMessage data based on the backend model
interface EmailMessage {
  id: number;
  subject: string;
  sender: string;
  recipients: string;
  cc_recipients?: string;
  sender_name?: string | null;
  body: string;
  thread_id?: string | null;
  received_at: string;
  person?: number | null;
  person_detail?: { id: number; name: string; is_self: boolean } | null;
  business?: number | null;
  business_detail?: { id: number; name: string } | null;
  project?: number | null;
  project_detail?: { id: number; name: string } | null;
  category?: number | null;
  is_handled: boolean;
  needs_internal_handling: boolean;
  waiting_external_handling: boolean;
  draft_reply?: string | null;
  needs_reply?: boolean;
  categories?: string[];
  is_newsletter?: boolean;
  source?: 'gmail' | 'mailgun' | 'manual';
  account_detail?: { id: number; email_address: string; display_name: string; provider: string; domain: string } | null;
  is_outgoing?: boolean;
  is_read: boolean;
  is_starred: boolean;
  is_archived: boolean;
}

interface Person {
  id: number;
  name: string;
  is_self?: boolean;
}

interface Classification {
  project_id: number | null;
  business_id: number | null;
  category_id: number | null;
  person_id: number | null;
  confidence: number;
  reasoning: string;
}

const fallbackEmails: EmailMessage[] = [
  { id: 1, subject: "Meeting Follow-up", sender: "client@example.com", sender_name: "Alice Wonderland", recipients: "me@example.com", body: "...", received_at: "2024-04-17T10:30:00Z", is_handled: false, needs_internal_handling: false, waiting_external_handling: false, person: 1, person_detail: {id: 1, name: "Alice", is_self: false}, needs_reply: true, categories: ["Client Communication"], is_read: false, is_starred: false, is_archived: false },
  { id: 2, subject: "Project Update", sender: "colleague@example.com", sender_name: "Bob The Colleague", recipients: "me@example.com, manager@example.com", body: "...", received_at: "2024-04-17T09:15:00Z", is_handled: true, needs_internal_handling: false, waiting_external_handling: false, person: null, person_detail: null, needs_reply: false, categories: ["Internal", "Project Alpha"], is_read: true, is_starred: false, is_archived: true },
  { id: 3, subject: "Invoice #123", sender: "accounting@example.com", sender_name: null, recipients: "me@example.com", body: "...", received_at: "2024-04-18T11:00:00Z", is_handled: false, needs_internal_handling: false, waiting_external_handling: false, person: null, person_detail: null, needs_reply: false, categories: ["Finance"], is_read: false, is_starred: true, is_archived: false },
];

export interface EmailListRef {
  refresh: () => void;
}

interface EmailListProps {
  accounts?: EmailAccount[];
  queryParams?: Record<string, string>;
  searchQuery?: string;
}

const EmailList = forwardRef<EmailListRef, EmailListProps>(({ accounts = [], queryParams = {}, searchQuery = '' }, ref) => {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedAccountFilter, setSelectedAccountFilter] = useState<string>('');
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<EmailMessage | null>(null);
  const [replyAllMode, setReplyAllMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningEmailId, setAssigningEmailId] = useState<number | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  const [createNewPerson, setCreateNewPerson] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  const [newPersonRelationship, setNewPersonRelationship] = useState('');
  const [newPersonNotes, setNewPersonNotes] = useState('');
  const [projects, setProjects] = useState<Array<{ id: number; name: string; business_id?: number | null }>>([]);
  const [businesses, setBusinesses] = useState<Array<{ id: number; name: string }>>([]);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [classifications, setClassifications] = useState<{ [key: number]: Classification }>({});
  const [classifyingEmails, setClassifyingEmails] = useState<{ [key: number]: boolean }>({});

  // Hover states
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);

  // Quick action modals state
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [expectationFormOpen, setExpectationFormOpen] = useState(false);
  const [quickActionEmail, setQuickActionEmail] = useState<EmailMessage | null>(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const pageSize = 50;

  // Ref to track current page for use in polling interval (avoids stale closure)
  const currentPageRef = React.useRef(currentPage);
  currentPageRef.current = currentPage;

  const fetchEmails = async (page = 1, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams();
      // Apply server-side query params from tabs
      Object.entries(queryParams).forEach(([key, value]) => {
        params.set(key, value);
      });
      // Apply search query
      if (searchQuery.trim()) {
        params.set('search', searchQuery.trim());
      }
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      const response = await apiClient.get(`/api/emails/?${params.toString()}`);
      // Handle paginated response: { count, next, previous, results }
      const data = response.data;
      if (data && data.results) {
        setEmails(data.results);
        setTotalCount(data.count || 0);
        setHasNextPage(!!data.next);
        setHasPrevPage(!!data.previous);
        setCurrentPage(page);
      } else {
        // Fallback for non-paginated response
        setEmails(data || []);
        setTotalCount((data || []).length);
      }
      if (!silent) {
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error("Error fetching emails:", err);
      if (!silent) {
        setError("Failed to load emails. Displaying sample data.");
        setEmails(fallbackEmails);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchProjectsAndBusinesses = async () => {
    try {
      const [projectsRes, businessesRes, categoriesRes] = await Promise.all([
        apiClient.get('/api/projects/'),
        apiClient.get('/api/businesses/'),
        apiClient.get('/api/categories/')
      ]);
      setProjects(projectsRes.data || []);
      setBusinesses(businessesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      console.error('Error fetching projects, businesses, and categories:', err);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      try {
        // Add a small delay to ensure backend has processed changes
        await new Promise(resolve => setTimeout(resolve, 100));
        setRefreshKey(prev => prev + 1);
      } catch (error) {
        console.error('[EmailList] Error during refresh:', error);
        setError('Failed to refresh email list');
      }
    }
  }));

  const queryParamsKey = JSON.stringify(queryParams);
  useEffect(() => {
    fetchEmails();
    fetchProjectsAndBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, queryParamsKey, searchQuery]);

  // Auto-refresh every 60 seconds — silent to avoid disrupting the user
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEmails(currentPageRef.current, { silent: true });
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParamsKey, searchQuery]);

  // Derive unique categories from emails (now strings) for the filter dropdown
  const availableCategories = useMemo(() => {
    const categorySet = new Set<string>();
    emails.forEach(email => {
      email.categories?.forEach(cat => {
        if (cat) { // Ensure category string is not empty/null
           categorySet.add(cat);
        }
      });
    });
    // Convert set to array and sort alphabetically
    return Array.from(categorySet).sort((a, b) => a.localeCompare(b));
  }, [emails]);

  const handleRowClick = (email: EmailMessage) => {
    setSelectedEmail(email);
    // Auto-mark as read when opening
    if (!email.is_read) {
      apiClient.post(`/api/emails/${email.id}/mark_read/`).catch(() => {});
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
    }
  };

  const handleToggleStar = async (e: React.MouseEvent, emailId: number) => {
    e.stopPropagation();
    try {
      const res = await apiClient.post(`/api/emails/${emailId}/toggle_star/`);
      setEmails(prev => prev.map(em => em.id === emailId ? { ...em, is_starred: res.data.is_starred } : em));
    } catch (err) {
      console.error('Failed to toggle star:', err);
    }
  };

  const handleBlockSender = async (emailId: number, blockType: 'email' | 'domain' = 'email') => {
    try {
      const res = await apiClient.post(`/api/emails/${emailId}/block_sender/`, { block_type: blockType });
      if (res.data.status === 'blocked') {
        // Remove archived emails from current view
        setEmails(prev => prev.filter(em => {
          if (blockType === 'domain') {
            const domain = res.data.value;
            return !em.sender.toLowerCase().endsWith(`@${domain}`);
          }
          return em.sender.toLowerCase() !== res.data.value;
        }));
        handleCloseModal();
      }
    } catch (err) {
      console.error('Failed to block sender:', err);
    }
  };

  const handleMarkNewsletter = async (emailId: number) => {
    try {
      const res = await apiClient.post(`/api/emails/${emailId}/mark_newsletter/`);
      if (res.data.status === 'marked') {
        const sender = res.data.sender;
        setEmails(prev => prev.filter(em => em.sender.toLowerCase() !== sender));
        handleCloseModal();
      }
    } catch (err) {
      console.error('Failed to mark newsletter:', err);
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedIds.size === 0) return;
    try {
      await apiClient.post('/api/emails/bulk_action/', { ids: Array.from(selectedIds), action });
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Bulk action failed:', err);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEmails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEmails.map(e => e.id)));
    }
  };

  const toggleSelect = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCloseModal = () => {
    setSelectedEmail(null);
  };

  // Handler for category checkbox changes
  const handleCategoryCheckboxChange = (categoryName: string, isChecked: boolean) => {
    setSelectedCategories(prevSelected => {
      if (isChecked) {
        // Add category if checked and not already present
        return [...prevSelected, categoryName];
      } else {
        // Remove category if unchecked
        return prevSelected.filter(cat => cat !== categoryName);
      }
    });
  };

  // Function to check if email is uncategorized (needs red background)
  const isEmailUncategorized = (email: EmailMessage): boolean => {
    return !email.is_handled && !email.needs_internal_handling && !email.waiting_external_handling;
  };

  // Filter emails based on client-side refinements (category, account)
  // Note: main filtering (handled, starred, archived, newsletters) is now server-side via tabs
  const filteredEmails = useMemo(() => {
    return emails.filter(email => {
      // Category Filter: Check if email has ANY of the selected categories
      if (selectedCategories.length > 0 &&
          !selectedCategories.some(selCat => email.categories?.includes(selCat))) {
        return false;
      }

      // Account Filter
      if (selectedAccountFilter && email.account_detail?.id !== parseInt(selectedAccountFilter)) {
        return false;
      }

      return true;
    });
  }, [emails, selectedCategories, selectedAccountFilter]);

  const openAssignModal = async (emailId: number) => {
    setAssigningEmailId(emailId);
    setAssignModalOpen(true);
    setAssignError(null);
    setAssignSuccess(null);
    setSelectedPersonId(null);
    setCreateNewPerson(false);
    setNewPersonName('');
    setNewPersonRelationship('');
    setNewPersonNotes('');
    try {
      const res = await apiClient.get('/api/people/');
      setPeople(res.data || []);
    } catch (err) {
      setAssignError('Failed to load people.');
    }
  };

  const handleAssign = async () => {
    if (!assigningEmailId) return;

    // Check if we're creating a new person or assigning to existing
    if (createNewPerson) {
      if (!newPersonName.trim()) {
        setAssignError('Person name is required');
        return;
      }
    } else {
      if (!selectedPersonId) {
        setAssignError('Please select a person');
        return;
      }
    }

    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccess(null);

    try {
      // Find the email object to get the sender address
      const email = emails.find(e => e.id === assigningEmailId);
      if (!email) throw new Error('Email not found');

      let personData: { id: number; name: string; is_self?: boolean } | undefined;

      if (createNewPerson) {
        // Create new person and assign email
        const response = await apiClient.post('/api/people/create_and_assign_email/', {
          name: newPersonName.trim(),
          email_address: email.sender,
          relationship: newPersonRelationship.trim(),
          notes: newPersonNotes.trim()
        });
        personData = response.data.person;
        setAssignSuccess(`Created new person "${newPersonName}" and assigned email!`);
      } else {
        // Assign to existing person
        await apiClient.post(`/api/people/${selectedPersonId}/assign_email_address/`, {
          email_address: email.sender
        });
        // Get person data from the people list for existing person assignment
        const selectedPerson = people.find(p => p.id === selectedPersonId);
        personData = selectedPerson;
        setAssignSuccess('Sender assigned successfully!');
      }

      // Immediately update the email's person data in local state
      if (personData) {
        const personId = personData.id;
        const person_detail = { id: personData.id, name: personData.name, is_self: personData.is_self || false };
        setEmails(prevEmails =>
          prevEmails.map(e =>
            e.sender === email.sender
              ? { ...e, person: personId, person_detail }
              : e
          )
        );
      }

      setTimeout(() => {
        setAssignModalOpen(false);
        setAssigningEmailId(null);
        setAssignSuccess(null);
        setSelectedPersonId(null);
        setCreateNewPerson(false);
        setNewPersonName('');
        setNewPersonRelationship('');
        setNewPersonNotes('');
        setRefreshKey(prev => prev + 1); // Refresh email list
      }, 1500);
    } catch (err: any) {
      console.error('Failed to assign sender:', err);
      const errorMessage = err.response?.data?.error || 'Failed to assign sender.';
      setAssignError(errorMessage);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleEmailStatusChange = async (emailId: number, isHandled: boolean) => {
    try {

      // Log current email state before update
      const currentEmail = emails.find(e => e.id === emailId);

      if (isHandled) {

        // Use the specific endpoint for marking as handled
        await apiClient.post(`/api/emails/${emailId}/mark_handled/`);


        // Update local state to reflect the change immediately
        // Backend clears needs_internal_handling and waiting_external_handling when marking as handled
        // If current view filters out handled emails, remove it; otherwise update in place
        if (queryParams.is_handled === 'false') {
          setEmails(prevEmails => prevEmails.filter(e => e.id !== emailId));
        } else {
          setEmails(prevEmails => prevEmails.map(email =>
            email.id === emailId ? { ...email, is_handled: true, needs_internal_handling: false, waiting_external_handling: false } : email
          ));
        }


      } else {

        // For unhandling, use PATCH to set is_handled to false
        await apiClient.patch(`/api/emails/${emailId}/`, {
          is_handled: false
        });


        // Update local state
        setEmails(prevEmails => prevEmails.map(email =>
          email.id === emailId ? {
            ...email,
            is_handled: false
          } : email
        ));
      }


    } catch (err) {
      console.error('[EmailList] === EMAIL STATUS CHANGE ERROR ===');
      console.error('[EmailList] Failed to update email status:', err);
      const errorAny = err as any;
      if (errorAny.response) {
        console.error('[EmailList] API Error details:', {
          status: errorAny.response?.status,
          statusText: errorAny.response?.statusText,
          data: errorAny.response?.data,
        });
      }
      console.error('[EmailList] === END ERROR ===');
    }
  };

  const handleHandlingTypeChange = async (emailId: number, handlingType: 'external' | 'internal') => {
    try {

      // Log current email state before update
      const currentEmail = emails.find(e => e.id === emailId);

      const endpoint = handlingType === 'external'
        ? `/api/emails/${emailId}/mark_external_handling/`
        : `/api/emails/${emailId}/mark_internal_handling/`;

      await apiClient.post(endpoint);


      // Update local state to reflect the change immediately
      setEmails(prevEmails => {
        const updatedEmails = prevEmails.map(email =>
          email.id === emailId ? {
            ...email,
            needs_internal_handling: handlingType === 'internal',
            waiting_external_handling: handlingType === 'external',
            is_handled: false // Don't set as handled when marking as internal/external
          } : email
        );
        const updatedEmail = updatedEmails.find(e => e.id === emailId);
        return updatedEmails;
      });

    } catch (err) {
      console.error(`[EmailList] Failed to mark email as ${handlingType} handling:`, err);
    }
  };

  const handleProjectChange = async (emailId: number, projectId: number | null) => {
    try {
      const selectedProject = projectId ? projects.find(p => p.id === projectId) : null;
      const businessId = selectedProject?.business_id || null;

      await apiClient.patch(`/api/emails/${emailId}/`, {
        project: projectId,
        business: businessId
      });

      // Update local state
      setEmails(prevEmails => prevEmails.map(email =>
        email.id === emailId
          ? {
              ...email,
              project: projectId,
              project_detail: selectedProject ? { id: selectedProject.id, name: selectedProject.name } : null,
              business: businessId,
              business_detail: businessId
                ? businesses.find(b => b.id === businessId) || null
                : null
            }
          : email
      ));
    } catch (err) {
      console.error('Failed to update email project:', err);
    }
  };

  const handleBusinessChange = async (emailId: number, businessId: number | null) => {
    try {
      await apiClient.patch(`/api/emails/${emailId}/`, {
        business: businessId
      });

      // Update local state
      setEmails(prevEmails => prevEmails.map(email =>
        email.id === emailId
          ? {
              ...email,
              business: businessId,
              business_detail: businessId
                ? businesses.find(b => b.id === businessId) || null
                : null
            }
          : email
      ));
    } catch (err) {
      console.error('Failed to update email business:', err);
    }
  };

  const handleCategoryChange = async (emailId: number, categoryId: number | null) => {
    try {
      await apiClient.patch(`/api/emails/${emailId}/`, {
        category: categoryId
      });

      // Update local state
      setEmails(prevEmails => prevEmails.map(email =>
        email.id === emailId
          ? {
              ...email,
              category: categoryId
            }
          : email
      ));
    } catch (err) {
      console.error('Failed to update email category:', err);
    }
  };

  const handleClassify = async (emailId: number) => {
    setClassifyingEmails(prev => ({ ...prev, [emailId]: true }));
    try {
      const response = await apiClient.post(`/api/emails/${emailId}/classify/`);
      setClassifications(prev => ({
        ...prev,
        [emailId]: response.data.classification
      }));
    } catch (error) {
      console.error('Error classifying email:', error);
      alert('Failed to classify email. Please try again.');
    } finally {
      setClassifyingEmails(prev => ({ ...prev, [emailId]: false }));
    }
  };

  const handleApplyClassification = async (emailId: number) => {
    const classification = classifications[emailId];
    if (!classification) return;

    try {
      await apiClient.post(`/api/emails/${emailId}/apply-classification/`, {
        project_id: classification.project_id,
        business_id: classification.business_id,
        category_id: classification.category_id,
        person_id: classification.person_id,
        reasoning: classification.reasoning
      });

      // Update email in-place without full reload
      setEmails(prevEmails => prevEmails.map(email => {
        if (email.id === emailId) {
          // Find the person and construct person_detail with is_self
          const person = classification.person_id ? people.find(p => p.id === classification.person_id) : null;
          const person_detail = person ? { id: person.id, name: person.name, is_self: person.is_self || false } : null;

          return {
            ...email,
            project: classification.project_id,
            business: classification.business_id,
            category: classification.category_id,
            person: classification.person_id,
            // Update detail objects if we have them
            project_detail: classification.project_id ? projects.find(p => p.id === classification.project_id) || null : null,
            business_detail: classification.business_id ? businesses.find(b => b.id === classification.business_id) || null : null,
            person_detail: person_detail,
          };
        }
        return email;
      }));

      // Clear classification
      setClassifications(prev => {
        const newClassifications = { ...prev };
        delete newClassifications[emailId];
        return newClassifications;
      });

    } catch (error) {
      console.error('Error applying classification:', error);
      // Only show error, no success message
      alert('Failed to apply classification');
    }
  };

  const handleDismissClassification = (emailId: number) => {
    setClassifications(prev => {
      const newClassifications = { ...prev };
      delete newClassifications[emailId];
      return newClassifications;
    });
  };


  // Helper to check if email should show classify button
  const shouldShowClassifyButton = (email: EmailMessage) => {
    // Don't show if already handled
    if (email.is_handled) return false;
    // Don't show if already has a project (project is the main classification)
    if (email.project) return false;
    // Don't show if has business AND category (sufficiently classified without project)
    if (email.business && email.category) return false;
    return true;
  };


  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}

      {/* Header + Filters — single compact bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12], flexWrap: 'wrap', paddingBottom: SPACING[8], borderBottom: `1px solid ${COLORS.border.default}` }}>
        {/* Title + count */}
        <div style={{ display: 'flex', alignItems: 'center', marginRight: SPACING[4] }}>
          <InboxIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4], color: COLORS.accent.default }} />
          <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>Emails</span>
          <span style={{ marginLeft: SPACING[4], fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
            {filteredEmails.length}/{totalCount}
          </span>
        </div>

        {/* Category chips (inline, only if categories exist) */}
        {availableCategories.length > 0 && (
          <>
            <span style={{ width: '1px', height: '16px', backgroundColor: COLORS.border.default }} />
            {availableCategories.map(cat => (
              <label
                key={cat}
                htmlFor={`cat-${cat}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'pointer',
                  padding: `1px ${SPACING[8]}`, borderRadius: RADIUS,
                  fontSize: TYPOGRAPHY.body.sizes.tiny,
                  color: selectedCategories.includes(cat) ? COLORS.status.info : COLORS.text.secondary,
                  backgroundColor: selectedCategories.includes(cat) ? 'rgba(99,102,241,0.08)' : 'transparent',
                  border: `1px solid ${selectedCategories.includes(cat) ? 'rgba(99,102,241,0.25)' : 'transparent'}`,
                  whiteSpace: 'nowrap',
                }}
              >
                <input
                  id={`cat-${cat}`}
                  type="checkbox"
                  checked={selectedCategories.includes(cat)}
                  onChange={e => handleCategoryCheckboxChange(cat, e.target.checked)}
                  style={{ width: '11px', height: '11px', margin: 0 }}
                />
                {cat}
              </label>
            ))}
          </>
        )}

        {/* Account filter dropdown */}
        {accounts.length > 0 && (
          <>
            <span style={{ width: '1px', height: '16px', backgroundColor: COLORS.border.default }} />
            <select
              value={selectedAccountFilter}
              onChange={e => setSelectedAccountFilter(e.target.value)}
              style={{
                border: `1px solid ${selectedAccountFilter ? 'rgba(59,130,246,0.25)' : COLORS.border.default}`,
                borderRadius: RADIUS,
                padding: `1px ${SPACING[8]}`,
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                color: selectedAccountFilter ? COLORS.accent.default : COLORS.text.secondary,
                backgroundColor: selectedAccountFilter ? 'rgba(59,130,246,0.08)' : 'transparent',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="">All accounts</option>
              {accounts.map(a => (
                <option key={a.id} value={String(a.id)}>{a.email_address}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {filteredEmails.length === 0 && !error && !loading && (
         <div style={{ textAlign: 'center', padding: `${SPACING[32]} 0`, backgroundColor: COLORS.bg.base, borderRadius: RADIUS }}>
            <InboxIcon style={{ margin: '0 auto', width: '48px', height: '48px', color: COLORS.text.tertiary }} />
            <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
                {selectedCategories.length > 0 || selectedAccountFilter ? 'No emails match the current filters.' : 'No emails found.'}
            </p>
         </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING[8],
          padding: `${SPACING[8]} ${SPACING[12]}`, marginBottom: SPACING[8],
          backgroundColor: '#EFF6FF', borderRadius: RADIUS, border: `1px solid ${COLORS.accent.default}`,
        }}>
          <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
            {selectedIds.size} selected
          </span>
          <button onClick={() => handleBulkAction('archive')} style={{ fontSize: TYPOGRAPHY.body.sizes.small, padding: '2px 8px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer' }}>Archive</button>
          <button onClick={() => handleBulkAction('mark_read')} style={{ fontSize: TYPOGRAPHY.body.sizes.small, padding: '2px 8px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer' }}>Mark Read</button>
          <button onClick={() => handleBulkAction('mark_handled')} style={{ fontSize: TYPOGRAPHY.body.sizes.small, padding: '2px 8px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer' }}>Mark Handled</button>
          <button onClick={() => handleBulkAction('star')} style={{ fontSize: TYPOGRAPHY.body.sizes.small, padding: '2px 8px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer' }}>Star</button>
          <button onClick={() => setSelectedIds(new Set())} style={{ fontSize: TYPOGRAPHY.body.sizes.small, padding: '2px 8px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer' }}>Clear</button>
        </div>
      )}

      {/* Compact two-line row list */}
      {filteredEmails.length > 0 && (
        <div style={{ border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, backgroundColor: COLORS.bg.panel, overflow: 'hidden' }}>
          {/* Select all header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: SPACING[8],
            padding: `${SPACING[4]} ${SPACING[12]}`,
            borderBottom: `1px solid ${COLORS.border.default}`,
            backgroundColor: COLORS.bg.hover,
          }}>
            <input
              type="checkbox"
              checked={selectedIds.size === filteredEmails.length && filteredEmails.length > 0}
              onChange={toggleSelectAll}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary }}>
              {selectedIds.size > 0 ? `${selectedIds.size} of ${filteredEmails.length}` : `Select all (${filteredEmails.length})`}
            </span>
          </div>
          {filteredEmails.map((email, index) => {
            const statusValue = email.is_handled ? 'handled' : email.needs_internal_handling ? 'internal' : email.waiting_external_handling ? 'external' : '';
            const uncategorized = isEmailUncategorized(email);
            const categoryObj = email.category ? categories.find(c => c.id === email.category) : null;
            return (
              <div
                key={email.id}
                onClick={() => handleRowClick(email)}
                onMouseEnter={() => setHoveredRowId(email.id)}
                onMouseLeave={() => setHoveredRowId(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[12],
                  padding: `${SPACING[8]} ${SPACING[12]}`,
                  cursor: 'pointer',
                  transition: 'background-color 100ms',
                  backgroundColor: uncategorized
                    ? (hoveredRowId === email.id ? '#FEE2E2' : '#FEF2F2')
                    : (hoveredRowId === email.id ? '#EFF6FF' : 'transparent'),
                  borderBottom: index < filteredEmails.length - 1 ? `1px solid ${COLORS.border.default}` : 'none',
                }}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedIds.has(email.id)}
                  onClick={(e) => toggleSelect(e, email.id)}
                  onChange={() => {}}
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                />

                {/* Unread dot */}
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: email.is_read ? 'transparent' : COLORS.accent.default,
                }} />

                {/* Star toggle */}
                <div
                  onClick={(e) => handleToggleStar(e, email.id)}
                  style={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                >
                  {email.is_starred
                    ? <StarIconSolid style={{ width: 16, height: 16, color: '#F59E0B' }} />
                    : <StarIconOutline style={{ width: 16, height: 16, color: COLORS.text.tertiary }} />
                  }
                </div>

                {/* Main column: Subject (line 1) + Sender · Status · Person · Category (line 2) */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Line 1: Subject */}
                  <div
                    style={{
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: email.is_read ? TYPOGRAPHY.weight.normal : TYPOGRAPHY.weight.semibold,
                      color: email.is_read ? COLORS.text.secondary : COLORS.text.primary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={email.subject}
                  >
                    {email.subject}
                  </div>
                  {/* Line 2: Sender · Status · Person · Category · Newsletter */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], marginTop: '2px', flexWrap: 'wrap' }}>
                    {/* Sender */}
                    <span
                      style={{
                        fontSize: TYPOGRAPHY.body.sizes.tiny,
                        color: COLORS.text.secondary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '160px',
                      }}
                      title={email.is_outgoing ? `To: ${email.recipients}` : (email.sender_name ?? email.sender)}
                    >
                      {email.is_outgoing ? `To: ${email.recipients.split(',')[0].replace(/<[^>]+>/g, '').trim()}` : (email.sender_name ?? email.sender)}
                    </span>

                    {/* Source badge */}
                    {email.source && email.source !== 'gmail' && (
                      <span style={{
                        fontSize: '9px',
                        padding: '0 4px',
                        borderRadius: '3px',
                        backgroundColor: email.source === 'mailgun' ? 'rgba(249,115,22,0.1)' : 'rgba(107,114,128,0.1)',
                        color: email.source === 'mailgun' ? '#F97316' : COLORS.text.tertiary,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {email.source}
                      </span>
                    )}

                    <span style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.tiny }}>·</span>

                    {/* Status buttons (one-click) */}
                    {statusValue === 'handled' ? (
                      <span
                        onClick={e => { e.stopPropagation(); handleEmailStatusChange(email.id, false); }}
                        style={{
                          padding: `1px ${SPACING[8]}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: '#DCFCE7',
                          color: '#166534',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Handled
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: '2px' }}>
                        <span
                          onClick={e => { e.stopPropagation(); handleEmailStatusChange(email.id, true); }}
                          style={{
                            padding: `1px ${SPACING[4]}`,
                            borderRadius: RADIUS,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            backgroundColor: statusValue === '' ? '#FEE2E2' : COLORS.bg.panel,
                            color: statusValue === '' ? '#B91C1C' : COLORS.text.secondary,
                            border: `1px solid ${statusValue === '' ? '#FCA5A5' : COLORS.border.default}`,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {statusValue === 'internal' ? 'Int' : statusValue === 'external' ? 'Ext' : 'Mark Handled'}
                        </span>
                      </span>
                    )}

                    {/* Person badge */}
                    {email.person_detail ? (
                      <Link
                        to={`/people/${email.person_detail.id}`}
                        onClick={e => e.stopPropagation()}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `1px ${SPACING[8]}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          textDecoration: 'none',
                          backgroundColor: email.person_detail.is_self ? '#DBEAFE' : '#DCFCE7',
                          color: email.person_detail.is_self ? '#1E40AF' : '#166534',
                          whiteSpace: 'nowrap',
                        }}
                        title={`View ${email.person_detail.name}`}
                      >
                        <UserCircleIcon style={{ width: '12px', height: '12px', marginRight: '2px' }} />
                        {email.person_detail.name}
                      </Link>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); openAssignModal(email.id); }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `1px ${SPACING[8]}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: COLORS.bg.hover,
                          color: COLORS.text.secondary,
                          border: 'none',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        title="Assign sender to person"
                      >
                        <UserCircleIcon style={{ width: '12px', height: '12px', marginRight: '2px' }} />
                        Assign
                      </button>
                    )}

                    {/* Category badge */}
                    {categoryObj && (
                      <>
                        <span style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.tiny }}>·</span>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `1px ${SPACING[8]}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: 'rgba(99, 102, 241, 0.1)',
                          color: COLORS.status.info,
                          whiteSpace: 'nowrap',
                        }}>
                          {categoryObj.name}
                        </span>
                      </>
                    )}

                    {/* Newsletter badge */}
                    {email.is_newsletter && (
                      <>
                        <span style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.tiny }}>·</span>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `1px ${SPACING[8]}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          backgroundColor: '#FFEDD5',
                          color: '#9A3412',
                          whiteSpace: 'nowrap',
                        }}>
                          <NewspaperIcon style={{ width: '10px', height: '10px', marginRight: '2px' }} />
                          NL
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Meta column: Project + Business dropdowns stacked */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px', width: '130px' }}>
                  <select
                    value={email.project_detail?.id || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      const projectId = e.target.value ? parseInt(e.target.value) : null;
                      handleProjectChange(email.id, projectId);
                    }}
                    style={{
                      width: '100%',
                      border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      padding: `1px ${SPACING[4]}`,
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      color: email.project_detail ? COLORS.text.primary : COLORS.text.tertiary,
                    }}
                  >
                    <option value="">No Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                  <select
                    value={email.business_detail?.id || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      const businessId = e.target.value ? parseInt(e.target.value) : null;
                      handleBusinessChange(email.id, businessId);
                    }}
                    style={{
                      width: '100%',
                      border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      padding: `1px ${SPACING[4]}`,
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      color: email.business_detail ? COLORS.text.primary : COLORS.text.tertiary,
                    }}
                  >
                    <option value="">No Business</option>
                    {businesses.map(business => (
                      <option key={business.id} value={business.id}>{business.name}</option>
                    ))}
                  </select>
                  {/* Category dropdown (below business) */}
                  <select
                    value={email.category || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      const categoryId = e.target.value ? parseInt(e.target.value) : null;
                      handleCategoryChange(email.id, categoryId);
                    }}
                    style={{
                      width: '100%',
                      border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      padding: `1px ${SPACING[4]}`,
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      color: email.category ? COLORS.text.primary : COLORS.text.tertiary,
                    }}
                  >
                    <option value="">No Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                {/* Right column: Date (line 1) + Action buttons (line 2) */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', minWidth: '90px' }}>
                  {/* Date */}
                  <span style={{
                    fontSize: TYPOGRAPHY.body.sizes.tiny,
                    color: COLORS.text.tertiary,
                    whiteSpace: 'nowrap',
                  }}>
                    {format(parseISO(email.received_at), 'MMM d, h:mma').toLowerCase()}
                  </span>
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: SPACING[4], alignItems: 'center' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setQuickActionEmail(email); setTaskFormOpen(true); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: SPACING[4], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, backgroundColor: COLORS.bg.panel, cursor: 'pointer',
                        color: COLORS.accent.default,
                      }}
                      title="Create task from this email"
                    >
                      <ClipboardDocumentListIcon style={{ width: '12px', height: '12px' }} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setQuickActionEmail(email); setExpectationFormOpen(true); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: SPACING[4], border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS, backgroundColor: COLORS.bg.panel, cursor: 'pointer',
                        color: '#9333EA',
                      }}
                      title="Create expectation from this email"
                    >
                      <ClockIcon style={{ width: '12px', height: '12px' }} />
                    </button>
                    {shouldShowClassifyButton(email) && !classifications[email.id] && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleClassify(email.id); }}
                        disabled={classifyingEmails[email.id]}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          padding: SPACING[4], border: 'none',
                          borderRadius: RADIUS, backgroundColor: '#F3E8FF', cursor: classifyingEmails[email.id] ? 'not-allowed' : 'pointer',
                          color: '#6B21A8', opacity: classifyingEmails[email.id] ? 0.5 : 1,
                        }}
                        title="Use AI to classify this email"
                      >
                        <SparklesIcon style={{ width: '12px', height: '12px' }} />
                      </button>
                    )}
                    {classifications[email.id] && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleApplyClassification(email.id); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: `1px ${SPACING[4]}`, border: 'none',
                            fontSize: TYPOGRAPHY.body.sizes.tiny, fontWeight: TYPOGRAPHY.weight.medium,
                            borderRadius: RADIUS, color: '#FFFFFF', backgroundColor: '#9333EA', cursor: 'pointer',
                          }}
                          title={`Apply: ${classifications[email.id].reasoning}`}
                        >
                          Apply
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDismissClassification(email.id); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: SPACING[4], border: `1px solid ${COLORS.border.default}`,
                            borderRadius: RADIUS, color: COLORS.text.secondary, backgroundColor: COLORS.bg.panel, cursor: 'pointer',
                          }}
                        >
                          <XMarkIcon style={{ width: '10px', height: '10px' }} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination controls */}
      {totalCount > pageSize && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: SPACING[12],
          padding: `${SPACING[12]} 0`, borderTop: `1px solid ${COLORS.border.default}`,
        }}>
          <button
            disabled={!hasPrevPage}
            onClick={() => fetchEmails(currentPage - 1)}
            style={{
              padding: `${SPACING[4]} ${SPACING[12]}`, fontSize: TYPOGRAPHY.body.sizes.small,
              border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS,
              backgroundColor: COLORS.bg.panel, cursor: hasPrevPage ? 'pointer' : 'default',
              opacity: hasPrevPage ? 1 : 0.4, color: COLORS.text.primary,
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
            Page {currentPage} of {Math.ceil(totalCount / pageSize)}
          </span>
          <button
            disabled={!hasNextPage}
            onClick={() => fetchEmails(currentPage + 1)}
            style={{
              padding: `${SPACING[4]} ${SPACING[12]}`, fontSize: TYPOGRAPHY.body.sizes.small,
              border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS,
              backgroundColor: COLORS.bg.panel, cursor: hasNextPage ? 'pointer' : 'default',
              opacity: hasNextPage ? 1 : 0.4, color: COLORS.text.primary,
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Email Detail Modal */}
      <EmailDetailModal
        email={selectedEmail}
        onClose={handleCloseModal}
        onMarkHandled={(emailId) => {
          handleEmailStatusChange(emailId, true);
          handleCloseModal();
        }}
        onBlockSender={(emailId, blockType) => handleBlockSender(emailId, blockType)}
        onMarkNewsletter={(emailId) => handleMarkNewsletter(emailId)}
        onReply={(email) => {
          setReplyAllMode(false);
          setReplyToEmail(email as EmailMessage);
          setReplyModalOpen(true);
          handleCloseModal();
        }}
        onReplyAll={(email) => {
          setReplyAllMode(true);
          setReplyToEmail(email as EmailMessage);
          setReplyModalOpen(true);
          handleCloseModal();
        }}
      />

      <Transition appear show={assignModalOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={() => setAssignModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)' }} />
          </Transition.Child>
          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{ display: 'flex', minHeight: '100%', alignItems: 'center', justifyContent: 'center', padding: SPACING[16], textAlign: 'center' }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{ width: '100%', maxWidth: '448px', transform: 'none', overflow: 'hidden', borderRadius: RADIUS, backgroundColor: COLORS.bg.panel, padding: SPACING[24], textAlign: 'left', verticalAlign: 'middle' }}>
                  <Dialog.Title as="h3" style={{ fontSize: TYPOGRAPHY.size.lg, fontWeight: TYPOGRAPHY.weight.semibold, lineHeight: '1.5', color: COLORS.text.primary, display: 'flex', alignItems: 'center' }}>
                    <UserCircleIcon style={{ width: '24px', height: '24px', color: COLORS.accent.default, marginRight: SPACING[8] }} />
                    Assign Sender to Person
                  </Dialog.Title>

                  {/* Toggle between existing person and create new */}
                  <div style={{ marginTop: SPACING[16] }}>
                    <div style={{ display: 'flex', gap: SPACING[16], marginBottom: SPACING[16] }}>
                      <Button
                        type="button"
                        onClick={() => setCreateNewPerson(false)}
                        variant={!createNewPerson ? "primary" : "ghost"}
                        fullWidth
                        style={!createNewPerson ? { backgroundColor: '#EFF6FF', color: COLORS.accent.default, border: `1px solid #BFDBFE` } : { border: `1px solid ${COLORS.border.default}` }}
                      >
                        Assign to Existing
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setCreateNewPerson(true)}
                        variant={createNewPerson ? "primary" : "ghost"}
                        fullWidth
                        style={createNewPerson ? { backgroundColor: '#EFF6FF', color: COLORS.accent.default, border: `1px solid #BFDBFE` } : { border: `1px solid ${COLORS.border.default}` }}
                      >
                        Create New Person
                      </Button>
                    </div>

                    {!createNewPerson ? (
                      /* Existing Person Selection */
                      <div>
                        <label style={{ display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[8] }}>Select a person:</label>
                        <select
                          style={{ display: 'block', width: '100%', border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: `${SPACING[8]} ${SPACING[12]}` }}
                          value={selectedPersonId ?? ''}
                          onChange={e => setSelectedPersonId(Number(e.target.value))}
                        >
                          <option value="">-- Select --</option>
                          {people.map(person => (
                            <option key={person.id} value={person.id}>{person.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      /* Create New Person Form */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                        <div>
                          <label style={{ display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Name *</label>
                          <input
                            type="text"
                            style={{ display: 'block', width: '100%', border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: `${SPACING[8]} ${SPACING[12]}` }}
                            placeholder="Enter person's name"
                            value={newPersonName}
                            onChange={e => setNewPersonName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Relationship</label>
                          <input
                            type="text"
                            style={{ display: 'block', width: '100%', border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: `${SPACING[8]} ${SPACING[12]}` }}
                            placeholder="e.g., Client, Friend, Colleague"
                            value={newPersonRelationship}
                            onChange={e => setNewPersonRelationship(e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Notes</label>
                          <textarea
                            style={{ display: 'block', width: '100%', border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS, padding: `${SPACING[8]} ${SPACING[12]}` }}
                            rows={3}
                            placeholder="Any additional notes about this person"
                            value={newPersonNotes}
                            onChange={e => setNewPersonNotes(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {assignError && <Alert variant="error" style={{ marginTop: SPACING[8] }}>{assignError}</Alert>}
                  {assignSuccess && <Alert variant="success" style={{ marginTop: SPACING[8] }}>{assignSuccess}</Alert>}
                  <div style={{ marginTop: SPACING[24], display: 'flex', justifyContent: 'flex-end', gap: SPACING[8] }}>
                    <Button
                      variant="secondary"
                      onClick={() => setAssignModalOpen(false)}
                      disabled={assignLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleAssign}
                      disabled={
                        assignLoading ||
                        (!createNewPerson && !selectedPersonId) ||
                        (createNewPerson && !newPersonName.trim())
                      }
                      isLoading={assignLoading}
                      loadingText="Processing..."
                    >
                      {createNewPerson ? 'Create & Assign' : 'Assign'}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Task Form Modal - Quick Action */}
      <TaskForm
        isOpen={taskFormOpen}
        onClose={() => {
          setTaskFormOpen(false);
          setQuickActionEmail(null);
        }}
        onTaskCreated={() => {
          setTaskFormOpen(false);
          setQuickActionEmail(null);
        }}
        initialTaskData={quickActionEmail ? {
          id: 0,
          description: `Follow up: ${quickActionEmail.subject}`,
          due_date: null,
          is_done: false,
          completion_date: null,
          importance: 'should_do' as const,
          is_urgent: false,
          project: quickActionEmail.project_detail || null,
          business: quickActionEmail.business_detail || null,
          categories: [],
          responsible: quickActionEmail.person_detail ? [quickActionEmail.person_detail] : [],
          impacted: [],
          top_priority: false,
          email: quickActionEmail.id,
          created_at: '',
        } : null}
        isEditMode={false}
      />

      {/* Expectation Form Modal - Quick Action */}
      <ExpectationForm
        isOpen={expectationFormOpen}
        onClose={() => {
          setExpectationFormOpen(false);
          setQuickActionEmail(null);
        }}
        onExpectationCreated={() => {
          setExpectationFormOpen(false);
          setQuickActionEmail(null);
        }}
        prefillData={quickActionEmail ? {
          email_id: quickActionEmail.id,
          person_ids: quickActionEmail.person_detail ? [quickActionEmail.person_detail.id] : [],
          project_id: quickActionEmail.project_detail?.id,
          business_id: quickActionEmail.business_detail?.id,
        } : undefined}
      />

      {/* Reply compose modal */}
      <ComposeEmail
        isOpen={replyModalOpen}
        onClose={() => { setReplyModalOpen(false); setReplyToEmail(null); setReplyAllMode(false); }}
        onSent={() => { setRefreshKey(prev => prev + 1); }}
        replyTo={replyToEmail ? {
          id: replyToEmail.id,
          subject: replyToEmail.subject,
          sender: replyToEmail.sender,
          sender_name: replyToEmail.sender_name || '',
          body: replyToEmail.body,
          account_detail: replyToEmail.account_detail || null,
          cc_recipients: replyToEmail.cc_recipients || '',
          recipients: replyToEmail.recipients || '',
          replyAll: replyAllMode,
        } : null}
        accounts={accounts}
      />
    </div>
  );
});

export default EmailList;
